import React, { useState, useMemo } from 'react'
import { FaSortUp, FaSortDown, FaSort, FaChevronRight, FaChevronDown } from 'react-icons/fa'
import { formatEur } from './networkUtils'

export default function SuppliersView({ parts }) {
  const [sortCol, setSortCol] = useState('spend')
  const [sortDir, setSortDir] = useState('desc')
  const [expandedRows, setExpandedRows] = useState(new Set())

  // Aggregate by supplier
  const suppliers = useMemo(() => {
    const map = {}
    parts.forEach(part => {
      part.suppliers.forEach(s => {
        if (!map[s.name]) {
          map[s.name] = { name: s.name, parts: [], families: new Set(), spend: 0 }
        }
        map[s.name].parts.push(part)
        map[s.name].families.add(part.family)
        map[s.name].spend += s.spend || 0
      })
    })
    return Object.values(map).map(s => ({
      ...s,
      partsCount: s.parts.length,
      familiesCount: s.families.size,
    }))
  }, [parts])

  const sorted = useMemo(() => {
    return [...suppliers].sort((a, b) => {
      let av, bv
      switch (sortCol) {
        case 'parts': av = a.partsCount; bv = b.partsCount; break
        case 'families': av = a.familiesCount; bv = b.familiesCount; break
        case 'spend': av = a.spend; bv = b.spend; break
        default: av = a.name; bv = b.name; break
      }
      const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv))
      return sortDir === 'asc' ? cmp : -cmp
    })
  }, [suppliers, sortCol, sortDir])

  const handleSort = (col) => {
    if (col === sortCol) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortCol(col); setSortDir('desc') }
  }

  const toggleExpand = (name) => {
    setExpandedRows(prev => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100">
        <span className="text-sm font-semibold text-gray-700">{suppliers.length} Suppliers</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100">
              <th className="w-8 px-2"></th>
              <SortTh col="name" label="Supplier" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} align="left" />
              <SortTh col="parts" label="Parts" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
              <SortTh col="families" label="Families" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
              <SortTh col="spend" label="Total Spend" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
            </tr>
          </thead>
          <tbody>
            {sorted.map(sup => (
              <React.Fragment key={sup.name}>
                <tr className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer group">
                  <td className="px-2" onClick={(e) => { e.stopPropagation(); toggleExpand(sup.name) }}>
                    <span className={`inline-flex items-center justify-center w-5 h-5 rounded text-[10px] border transition-colors ${
                      expandedRows.has(sup.name) ? 'bg-[#1e5045] text-white border-[#1e5045]' : 'bg-gray-50 text-gray-400 border-gray-200 group-hover:border-gray-400'
                    }`}>
                      {expandedRows.has(sup.name) ? <FaChevronDown size={8} /> : <FaChevronRight size={8} />}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-medium text-gray-800">{sup.name}</td>
                  <td className="px-3 py-2 text-right text-gray-600">{sup.partsCount}</td>
                  <td className="px-3 py-2 text-right text-gray-600">{sup.familiesCount}</td>
                  <td className="px-3 py-2 text-right font-medium text-[#1e5045]">{formatEur(sup.spend)}</td>
                </tr>
                {expandedRows.has(sup.name) && (
                  <tr>
                    <td colSpan={5} className="p-0">
                      <ExpandedSupplierRow parts={sup.parts} supplierName={sup.name} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function ExpandedSupplierRow({ parts, supplierName }) {
  const sorted = [...parts].sort((a, b) => {
    const aSpend = a.suppliers.find(s => s.name === supplierName)?.spend || 0
    const bSpend = b.suppliers.find(s => s.name === supplierName)?.spend || 0
    return bSpend - aSpend
  })

  return (
    <div className="mx-4 mb-3 ml-9 border-l-3 border-[#1e5045] rounded-r-lg bg-gray-50 overflow-hidden">
      <table className="w-full text-[11px]">
        <thead>
          <tr className="bg-gray-100">
            <th className="px-3 py-1.5 text-left font-semibold text-gray-500 uppercase text-[9px]">Part</th>
            <th className="px-3 py-1.5 text-left font-semibold text-gray-500 uppercase text-[9px]">Family</th>
            <th className="px-3 py-1.5 text-right font-semibold text-gray-500 uppercase text-[9px]">Unit Cost</th>
            <th className="px-3 py-1.5 text-right font-semibold text-gray-500 uppercase text-[9px]">Alt. Sources</th>
            <th className="px-3 py-1.5 text-right font-semibold text-gray-500 uppercase text-[9px]">Spend</th>
          </tr>
        </thead>
        <tbody>
          {sorted.slice(0, 20).map(part => {
            const supData = part.suppliers.find(s => s.name === supplierName)
            const altCount = part.suppliers.length - 1
            return (
              <tr key={part.pn} className="border-b border-gray-100 last:border-0 hover:bg-white/50">
                <td className="px-3 py-1.5">
                  <div className="font-mono font-medium text-gray-700">{part.pn}</div>
                  {part.description && <div className="text-[10px] text-gray-400 truncate max-w-[180px]">{part.description}</div>}
                </td>
                <td className="px-3 py-1.5 text-gray-500 italic text-[10px] capitalize">
                  {part.family?.replace(/_/g, ' ') || '—'}
                </td>
                <td className="px-3 py-1.5 text-right font-mono text-gray-600">
                  {supData?.unit_cost ? `€${supData.unit_cost.toFixed(2)}` : '—'}
                </td>
                <td className="px-3 py-1.5 text-right">
                  {altCount > 0
                    ? <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded text-[10px]">{altCount} alt</span>
                    : <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-[10px]">Sole</span>
                  }
                </td>
                <td className="px-3 py-1.5 text-right font-medium text-[#1e5045]">
                  {formatEur(supData?.spend || 0)}
                </td>
              </tr>
            )
          })}
          {parts.length > 20 && (
            <tr><td colSpan={5} className="px-3 py-1.5 text-center text-gray-400 text-[10px]">
              + {parts.length - 20} more parts
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

function SortTh({ col, label, sortCol, sortDir, onSort, align = 'right' }) {
  const active = sortCol === col
  return (
    <th
      className={`px-3 py-2 text-${align} text-[10px] font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700`}
      onClick={() => onSort(col)}
    >
      <span className={`flex items-center ${align === 'left' ? 'justify-start' : 'justify-end'} gap-1`}>
        {label}
        {active
          ? sortDir === 'asc' ? <FaSortUp size={10} className="text-[#1e5045]" /> : <FaSortDown size={10} className="text-[#1e5045]" />
          : <FaSort size={10} className="text-gray-300" />
        }
      </span>
    </th>
  )
}
